import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { warehouseService } from '../../_services/warehouse.service';
import { stockClassificationService } from '../../_services/stockclassification.service';
import { materialTypeService } from '../../_services/materialttype.service';
import { Spin, AutoComplete } from 'antd';

// Example/test data if no prop is provided
const defaultWarehouseData = {
  racks: [
    {
      id: 'rack1',
      name: 'Rack 1',
      stockBins: [
        { id: 'bin1', name: 'Bin 1', availableStockQuantity: 10, allowedStockQuantity: 100 },
        { id: 'bin2', name: 'Bin 2', availableStockQuantity: 50, allowedStockQuantity: 100 },
      ]
    },
    {
      id: 'rack2',
      name: 'Rack 2',
      stockBins: [
        { id: 'bin3', name: 'Bin 3', availableStockQuantity: 80, allowedStockQuantity: 100 },
        { id: 'bin4', name: 'Bin 4', availableStockQuantity: 0, allowedStockQuantity: 100 },
      ]
    }
  ]
};

// Centralized color constants for bins and legend (match 2D UI)
const BIN_COLORS = {
  HIGH:   { label: 'High Stock (≥80%)',   color: 'RED' }, // red
  MEDIUM: { label: 'Medium Stock (≥50%)', color: 'ORANGE' }, // orange
  LOW:    { label: 'Low Stock (under 50%)', color: 'YELLOW' }, // yellow
  EMPTY:  { label: 'Empty',                color: 'GREEN' }, // green
};

// Get bin color based on stock quantity (match 2D UI)
function getBinColor(bin) {
  if (typeof bin.availableStockQuantity === 'number' && typeof bin.allowedStockQuantity === 'number') {
    if (bin.availableStockQuantity === 0) return BIN_COLORS.EMPTY.color;
    if (bin.availableStockQuantity >= bin.allowedStockQuantity) return BIN_COLORS.HIGH.color;
    if (bin.availableStockQuantity / bin.allowedStockQuantity >= 0.5) return BIN_COLORS.MEDIUM.color;
    return BIN_COLORS.LOW.color;
  }
  return BIN_COLORS.LOW.color;
}

const Warehouse3DSimple = () => {
  const mountRef = useRef();
  // 3D search filter state
  const [warehouses3D, setWarehouses3D] = useState([]);
  const [selectedWarehouse3D, setSelectedWarehouse3D] = useState('');
  const [racks3D, setRacks3D] = useState([]);
  const [selectedRack3D, setSelectedRack3D] = useState('');
  const [bins3D, setBins3D] = useState([]);
  const [selectedBin3D, setSelectedBin3D] = useState('');
  const [warehouseData, setWarehouseData] = useState(null);
  const [selectedBin, setSelectedBin] = useState(null);
  const [binDetails, setBinDetails] = useState(null);
  const [binDetailsLoading, setBinDetailsLoading] = useState(false);
  const [binDetailsPage, setBinDetailsPage] = useState(1);
  // Loading states for filters and warehouse data
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingRacks, setLoadingRacks] = useState(false);
  const [loadingBins, setLoadingBins] = useState(false);
  const [loadingWarehouseData, setLoadingWarehouseData] = useState(false);
  // --- Stock Category & Material Filters ---
  const [stockCategories, setStockCategories] = useState([]);
  const [selectedStockCategory, setSelectedStockCategory] = useState('');
  const [materialTypes, setMaterialTypes] = useState([]);
  const [selectedMaterialType, setSelectedMaterialType] = useState('');

  // Fetch warehouse suggestions on mount
  useEffect(() => {
    setLoadingWarehouses(true);
    warehouseService.getSuggestion().then(res => {
      setWarehouses3D(res.content || []);
      setLoadingWarehouses(false);
    }).catch(() => setLoadingWarehouses(false));
  }, []);

  // Fetch stock categories and material types on mount
  useEffect(() => {
    stockClassificationService.getSuggestion().then(res => {
      setStockCategories(res.content || []);
    });
    materialTypeService.getSuggestion().then(res => {
      setMaterialTypes(res.content || []);
    });
  }, []);

  // Fetch racks when warehouse changes
  useEffect(() => {
    if (selectedWarehouse3D) {
      setLoadingRacks(true);
      warehouseService.getRacksByWarehouseId(selectedWarehouse3D).then(res => {
        setRacks3D(res.content?.racks || []);
        setSelectedRack3D('');
        setBins3D([]);
        setSelectedBin3D('');
        setLoadingRacks(false);
      }).catch(() => setLoadingRacks(false));
    } else {
      setRacks3D([]);
      setSelectedRack3D('');
      setBins3D([]);
      setSelectedBin3D('');
      setLoadingRacks(false);
    }
  }, [selectedWarehouse3D]);

  // Update bins when rack changes
  useEffect(() => {
    if (selectedRack3D) {
      setLoadingBins(true);
      const rack = racks3D.find(r => r.id === selectedRack3D);
      setBins3D(rack?.stockBins || []);
      setSelectedBin3D('');
      setLoadingBins(false);
    } else {
      setBins3D([]);
      setSelectedBin3D('');
      setLoadingBins(false);
    }
  }, [selectedRack3D, racks3D]);

  // Fetch warehouse data for 3D scene on search or warehouse change
  const handle3DSearch = () => {
    setLoadingWarehouseData(true);
    warehouseService.fetchWarehouseData(
      selectedWarehouse3D || undefined,
      selectedRack3D || undefined,
      selectedBin3D || undefined,
      selectedStockCategory || undefined,
      selectedMaterialType || undefined
    ).then(data => {
      if (data && data.content && data.content[0]) {
        setWarehouseData(data.content[0]);
      } else {
        setWarehouseData(null);
      }
      setLoadingWarehouseData(false);
    }).catch(() => setLoadingWarehouseData(false));
  };

  // Auto-load warehouse data when warehouse changes
  useEffect(() => {
    if (selectedWarehouse3D) {
      handle3DSearch();
    }
    // eslint-disable-next-line
  }, [selectedWarehouse3D]);

  // Fetch bin details when selectedBin or binDetailsPage changes
  useEffect(() => {
    if (selectedBin && selectedBin.id) {
      setBinDetailsLoading(true);
      warehouseService.getBinDetails(selectedBin.id, binDetailsPage, 50, 'AVAILABLE')
        .then(data => {
          setBinDetails(data);
          setBinDetailsLoading(false);
        })
        .catch(() => {
          setBinDetails(null);
          setBinDetailsLoading(false);
        });
    } else {
      setBinDetails(null);
      setBinDetailsLoading(false);
    }
  }, [selectedBin, binDetailsPage]);

  // Reset page to 1 when a new bin is selected
  useEffect(() => {
    if (selectedBin) setBinDetailsPage(1);
  }, [selectedBin]);

  useEffect(() => {
    if (!warehouseData) {
      // Cleanup Three.js scene if present
      if (mountRef.current && mountRef.current.firstChild) {
        while (mountRef.current.firstChild) {
          mountRef.current.removeChild(mountRef.current.firstChild);
        }
      }
      return;
    }
    const dataToRender = warehouseData;
    const width = mountRef.current.clientWidth;
    const height = 400;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#181c2a');
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    mountRef.current.appendChild(renderer.domElement);

    // Add OrbitControls for mouse interaction
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.screenSpacePanning = false;
    controls.minDistance = 2;
    controls.maxDistance = 100;

    // Add a light gray floor
    const floorGeometry = new THREE.PlaneGeometry(100, 100);
    const floorMaterial = new THREE.MeshStandardMaterial({ color: '#23263a', roughness: 0.8, metalness: 0.2 });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = 0;
    scene.add(floor);

    // Lighting improvements
    const light = new THREE.DirectionalLight(0xffffff, 1.2);
    light.position.set(10, 20, 10);
    scene.add(light);
    scene.add(new THREE.AmbientLight(0xffffff, 1.1));

    // Row layout for racks: one rack per row
    const racksPerRow = 1;
    const rackSpacingX = 0;
    const rackSpacingZ = 9;
    const binSpacing = 1.2;
    const textureLoader = new THREE.TextureLoader();
    const clickableBinMeshes = [];

    dataToRender.racks.forEach((rack, rackIdx) => {
      const row = rackIdx; // Each rack is its own row
      const col = 0;
      const x = 0; // All racks aligned on X axis
      const z = row * rackSpacingZ;

      // Rack base (taller/thicker)
      const rackWidth = Math.max(2, (rack.stockBins?.length || 1) * binSpacing);
      const rackHeight = 1.2;
      const rackDepth = 2.2;
      let rackColor = '#ffe066';
      if (typeof window.getRackColor === 'function') {
        rackColor = window.getRackColor(rack);
      }
      const rackGeometry = new THREE.BoxGeometry(rackWidth, rackHeight, rackDepth);
      let rackMaterial;
      if (rack.imageUrl) {
        const texture = textureLoader.load(rack.imageUrl);
        rackMaterial = new THREE.MeshStandardMaterial({ map: texture });
      } else {
        rackMaterial = new THREE.MeshStandardMaterial({ color: rackColor });
      }
      const rackMesh = new THREE.Mesh(rackGeometry, rackMaterial);
      rackMesh.position.set(x, rackHeight / 2 + 0.1, z);
      scene.add(rackMesh);
      // Add white outline to rack
      const rackEdges = new THREE.EdgesGeometry(rackGeometry);
      const rackLine = new THREE.LineSegments(rackEdges, new THREE.LineBasicMaterial({ color: '#fff', linewidth: 2 }));
      rackLine.position.copy(rackMesh.position);
      scene.add(rackLine);

      // Add rack label (as a flat plane, always face camera)
      const rackLabelCanvas = document.createElement('canvas');
      rackLabelCanvas.width = 240; rackLabelCanvas.height = 60;
      const ctx = rackLabelCanvas.getContext('2d');
      ctx.fillStyle = 'rgba(30,30,30,0.98)'; // Opaque dark background
      ctx.fillRect(0, 0, 256, 64);
      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = '#ffe066';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = '#000';
      ctx.shadowBlur = 12;
      ctx.fillText(rack.name, 128, 32);
      const rackLabelTexture = new THREE.CanvasTexture(rackLabelCanvas);
      const rackLabelMat = new THREE.SpriteMaterial({ map: rackLabelTexture, depthTest: false });
      const rackLabel = new THREE.Sprite(rackLabelMat);
      rackLabel.scale.set(4, 1, 1); // Smaller label
      rackLabel.position.set(x, rackHeight - 1, z); // Slightly above rack
      scene.add(rackLabel);

      // Bins on top of rack
      (rack.stockBins || []).forEach((bin, binIdx) => {
        const binWidth = 0.9, binHeight = 0.9, binDepth = 1;
        const binGeometry = new THREE.BoxGeometry(binWidth, binHeight, binDepth);
        let binMaterial;
        if (bin.imageUrl) {
          const texture = textureLoader.load(bin.imageUrl);
          binMaterial = new THREE.MeshStandardMaterial({ map: texture });
        } else {
          binMaterial = new THREE.MeshStandardMaterial({ color: getBinColor(bin) });
        }
        const binMesh = new THREE.Mesh(binGeometry, binMaterial);
        binMesh.position.set(
          x - rackWidth / 2 + (binIdx + 0.5) * binSpacing,
          rackHeight + binHeight / 2 + 0.1,
          z
        );
        binMesh.userData.bin = bin;
        clickableBinMeshes.push(binMesh);
        scene.add(binMesh);
        // Add white outline to bin
        const binEdges = new THREE.EdgesGeometry(binGeometry);
        const binLine = new THREE.LineSegments(binEdges, new THREE.LineBasicMaterial({ color: '#fff', linewidth: 1 }));
        binLine.position.copy(binMesh.position);
        scene.add(binLine);

        // Bin label (as a flat plane, always face camera)
        const binLabelCanvas = document.createElement('canvas');
        binLabelCanvas.width = 64; binLabelCanvas.height = 32;
        const bctx = binLabelCanvas.getContext('2d');
        bctx.fillStyle = '#23263a'; bctx.fillRect(0, 0, 64, 32);
        bctx.font = 'bold 12px Arial'; bctx.fillStyle = '#fff';
        bctx.textAlign = 'center'; bctx.textBaseline = 'middle';
        bctx.fillText(bin.name, 32, 12);
        // Show availability below name
        bctx.font = '10px Arial'; bctx.fillStyle = '#ffe066';
        const avail = typeof bin.availableStockQuantity === 'number' ? bin.availableStockQuantity.toLocaleString() : (bin.availableStockQuantity ?? '');
        const allowed = typeof bin.allowedStockQuantity === 'number' ? bin.allowedStockQuantity.toLocaleString() : (bin.allowedStockQuantity ?? '');
        bctx.fillText(`${avail} / ${allowed}`, 32, 24);
        const binLabelTexture = new THREE.CanvasTexture(binLabelCanvas);
        const binLabelMat = new THREE.SpriteMaterial({ map: binLabelTexture });
        const binLabel = new THREE.Sprite(binLabelMat);
        binLabel.scale.set(0.9, 0.4, 1);
        binLabel.position.set(
          x - rackWidth / 2 + (binIdx + 0.5) * binSpacing,
          rackHeight + binHeight + 0.3,
          z
        );
        scene.add(binLabel);
      });
    });

    // Camera and controls
    const totalRows = Math.ceil(dataToRender.racks.length / racksPerRow);
    const centerX = ((racksPerRow - 1) * rackSpacingX) / 2;
    const centerZ = ((totalRows - 1) * rackSpacingZ) / 2;
    camera.position.set(centerX, 12, centerZ + 18);
    camera.lookAt(centerX, 0, centerZ);
    controls.target.set(centerX, 0, centerZ);
    controls.update();

    // Raycaster for bin clicks
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    function onClick(event) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(clickableBinMeshes);
      if (intersects.length > 0) {
        const bin = intersects[0].object.userData.bin;
        if (bin) {
          setSelectedBin(bin);
        }
      }
    }
    renderer.domElement.addEventListener('click', onClick);

    // Animation loop (labels always face camera)
    let frameId;
    const animate = () => {
      scene.traverse(obj => {
        if (obj.type === 'Sprite') {
          obj.lookAt(camera.position);
        }
      });
      controls.update();
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(frameId);
      controls.dispose();
      renderer.dispose();
      renderer.domElement.removeEventListener('click', onClick);
      mountRef.current.removeChild(renderer.domElement);
    };
  }, [warehouseData]);

  // Bin details popup/modal
  return (
    <div style={{ padding: 32, background: '#181c2a', borderRadius: 16, minHeight: 400, position: 'relative' }}>
      {/* Loader overlay for all API calls */}
      {(loadingWarehouses || loadingRacks || loadingBins || loadingWarehouseData) && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(24,28,42,0.7)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Spin tip="Loading..." size="large" style={{ color: '#ffe066' }} />
        </div>
      )}
      {/* 3D Filters UI - styled like 2D */}
      <div style={{ background: '#23263a', borderRadius: 12, padding: 24, marginLeft: 20, marginBottom: 32, boxShadow: '0 2px 12px #0002', width: '100%' }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#ffe066', marginBottom: 16, letterSpacing: 1 }}>Filters</div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap', width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <label htmlFor="warehouse-select-3d" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Warehouse</label>
            <select id="warehouse-select-3d"
              value={selectedWarehouse3D}
              onChange={e => { setSelectedWarehouse3D(e.target.value); setSelectedRack3D(''); setSelectedBin3D(''); }}
              style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}>
              <option value=''>All Warehouses</option>
              {loadingWarehouses && <option>Loading...</option>}
              {warehouses3D.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <label htmlFor="rack-select-3d" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Rack</label>
            <select id="rack-select-3d"
              value={selectedRack3D}
              onChange={e => { setSelectedRack3D(e.target.value); setSelectedBin3D(''); }}
              style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}
              disabled={!selectedWarehouse3D}>
              <option value=''>All Racks</option>
              {racks3D.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <label htmlFor="bin-select-3d" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Bin</label>
            <select id="bin-select-3d"
              value={selectedBin3D}
              onChange={e => setSelectedBin3D(e.target.value)}
              style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}
              disabled={!selectedRack3D}>
              <option value=''>All Bins</option>
              {bins3D.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <label htmlFor="stock-category-select-3d" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Stock Category</label>
            <select
              id="stock-category-select-3d"
              value={selectedStockCategory}
              onChange={e => setSelectedStockCategory(e.target.value)}
              style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}
            >
              <option value=''>All Categories</option>
              {stockCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <label htmlFor="material-type-select-3d" style={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}>Material</label>
            <AutoComplete
              id="material-type-select-3d"
              value={
                selectedMaterialType
                  ? (materialTypes.find(mat => mat.id === selectedMaterialType)?.name || '')
                  : ''
              }
              options={materialTypes.map(mat => ({ value: mat.id, label: mat.name }))}
              onSelect={(value, option) => setSelectedMaterialType(value)}
              onChange={input => {
                const found = materialTypes.find(mat => mat.name === input);
                if (found) setSelectedMaterialType(found.id);
                else if (input === '') setSelectedMaterialType('');
              }}
              placeholder="All Materials"
              style={{ color: '#222', background: '#fff', padding: 8, borderRadius: 6 }}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase())
              }
              allowClear
            />
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 220, marginTop: 24 }}>
            <button onClick={handle3DSearch} disabled={loadingWarehouseData} style={{ padding: '10px 28px', borderRadius: 8, background: '#388e3c', color: '#fff', fontWeight: 700, border: 'none', cursor: loadingWarehouseData ? 'not-allowed' : 'pointer', fontSize: 16, minWidth: 100, opacity: loadingWarehouseData ? 0.7 : 1 }}>Search</button>
            <button onClick={() => { setSelectedWarehouse3D(''); setSelectedRack3D(''); setSelectedBin3D(''); setRacks3D([]); setBins3D([]); setWarehouseData(null); }} disabled={loadingWarehouseData} style={{ padding: '8px 20px', borderRadius: 8, background: '#444', color: '#fff', fontWeight: 500, border: 'none', cursor: loadingWarehouseData ? 'not-allowed' : 'pointer', fontSize: 14, minWidth: 100, opacity: loadingWarehouseData ? 0.7 : 1 }}>Clear Filters</button>
          </div>
        </div>
        {/* Active Filter Summary */}
        {(selectedWarehouse3D || selectedRack3D || selectedBin3D) && (
          <div style={{ marginTop: 18, color: '#ffe066', fontWeight: 500, fontSize: 15 }}>
            Showing: {warehouses3D.find(w => w.id === selectedWarehouse3D)?.name || 'All Warehouses'}
            {selectedRack3D && <> / {racks3D.find(r => r.id === selectedRack3D)?.name || selectedRack3D}</>}
            {selectedBin3D && <> / {bins3D.find(b => b.id === selectedBin3D)?.name || selectedBin3D}</>}
          </div>
        )}
      </div>
      {/* Bin Stock Level Legend (like 2D) */}
      <div style={{ display: 'flex', gap: 24, marginLeft: 20, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ background: 'RED', width: 16, height: 16, display: 'inline-block', borderRadius: 4 }} />
          <span style={{ color: '#fff', borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 14, marginLeft: 2 }}>High Stock (≥80%)</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ background: 'ORANGE', width: 16, height: 16, display: 'inline-block', borderRadius: 4 }} />
          <span style={{ color: '#fff', borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 14, marginLeft: 2 }}>Medium Stock (≥50%)</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ background: 'YELLOW', width: 16, height: 16, display: 'inline-block', borderRadius: 4 }} />
          <span style={{ color: '#fff', borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 14, marginLeft: 2 }}>Low Stock (under 50%)</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ background: 'GREEN', width: 16, height: 16, display: 'inline-block', borderRadius: 4 }} />
          <span style={{ color: '#fff', borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 14, marginLeft: 2 }}>Empty</span>
        </span>
      </div>
      <h2 style={{ color: '#ffe066', textAlign: 'center', marginBottom: 24 }}>3D Warehouse View</h2>
      <div ref={mountRef} style={{ width: '100%', height: 400, margin: '0 auto' }} />
      {/* Show message if no warehouse data loaded and not loading */}
      {(!warehouseData && !loadingWarehouses && !loadingRacks && !loadingBins && !loadingWarehouseData) && (
        <div style={{ color: '#ffe066', textAlign: 'center', fontSize: 22, fontWeight: 600, marginTop: 80 }}>
          Please select a warehouse and click Search to view the 3D warehouse.
        </div>
      )}
      {selectedBin && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#23263a',
            borderRadius: 16,
            padding: 32,
            minWidth: 340,
            color: '#fff',
            boxShadow: '0 4px 32px #000a',
            position: 'relative',
            maxWidth: '90vw',
          }}>
            <button
              onClick={() => { setSelectedBin(null); setBinDetails(null); setBinDetailsPage(1); }}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: '#181c2a',
                color: '#ffe066',
                border: '2px solid #ffe066',
                fontWeight: 900,
                fontSize: 24,
                cursor: 'pointer',
                zIndex: 10,
              }}
              title="Close"
            >×</button>
            <h2 style={{ color: '#ffe066', marginBottom: 16 }}>Bin Details</h2>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{selectedBin.name}</div>
            {binDetailsLoading ? (
              <Spin tip="Loading..." style={{ color: '#ffe066', margin: 24 }} />
            ) : binDetails && binDetails.content && binDetails.content.length > 0 ? (
              <div style={{ maxHeight: 400, overflowY: 'auto', marginTop: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#181c2a', borderRadius: 8 }}>
                  <thead>
                    <tr>
                      {Object.keys(binDetails.content[0]).filter(key => !['id','status','tempNumber','grossWeight','netWeight'].includes(key)).map(key => (
                        <th key={key} style={{ color: '#ffe066', fontWeight: 700, padding: '8px 12px', borderBottom: '2px solid #1976d2', background: '#23263a', textTransform: 'capitalize', fontSize: 15 }}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {binDetails.content.map((row, i) => (
                      <tr key={i}>
                        {Object.keys(binDetails.content[0]).filter(key => !['id','status','tempNumber','grossWeight','netWeight'].includes(key)).map(key => (
                          <td key={key} style={{ color: '#fff', padding: '8px 12px', borderBottom: '1px solid #23263a', fontSize: 14 }}>
                            {typeof row[key] === 'number' && row[key] != null
                              ? row[key].toLocaleString()
                              : typeof row[key] === 'object' && row[key] !== null
                                ? JSON.stringify(row[key])
                                : row[key] != null && String(row[key]).length > 40
                                  ? String(row[key]).slice(0, 37) + '...'
                                  : row[key] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination */}
                {binDetails.pagination && binDetails.pagination.totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
                    <button
                      onClick={() => setBinDetailsPage(p => Math.max(1, p - 1))}
                      disabled={binDetailsPage === 1}
                      style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #ccc', background: binDetailsPage === 1 ? '#eee' : '#fff', color: '#333', cursor: binDetailsPage === 1 ? 'not-allowed' : 'pointer' }}
                    >Prev</button>
                    <span style={{ alignSelf: 'center', color: '#fff', fontWeight: 500 }}>Page {binDetailsPage} / {binDetails.pagination.totalPages}</span>
                    <button
                      onClick={() => setBinDetailsPage(p => Math.min(binDetails.pagination.totalPages, p + 1))}
                      disabled={binDetailsPage === binDetails.pagination.totalPages}
                      style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #ccc', background: binDetailsPage === binDetails.pagination.totalPages ? '#eee' : '#fff', color: '#333', cursor: binDetailsPage === binDetails.pagination.totalPages ? 'not-allowed' : 'pointer' }}
                    >Next</button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: '#d32f2f', fontWeight: 500, marginTop: 24 }}>No details found for this bin.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Warehouse3DSimple; 